import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { OTPVerificationForm } from '@/components/OTPVerificationForm/OTPVerificationForm'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useVerifyProspectEmailMutation,
  useResendProspectOtpMutation,
} from '@/store/api/PublicForm/PublicForm.query'

const LS_KEY = 'prospect_otp_sent_at'

interface LocationState {
  email?: string
  product?: string
}

export const ProspectEmailVerification = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState
  const email = state?.email || ''
  const product = state?.product

  const [verifyOtp, { isLoading: isLoadingVerify }] =
    useVerifyProspectEmailMutation()
  const [resendOtp, { isLoading: isLoadingResend }] =
    useResendProspectOtpMutation()

  const handleVerify = async (otp: string) => {
    try {
      await verifyOtp({ email, otp }).unwrap()
      toast.success(t('feedback.toast.emailVerified'))
      localStorage.removeItem(LS_KEY)
      navigate(AppRoutes.publicPaymentPaywall, { state: { email, product } })
    } catch (error: any) {
      catchWithSentry(error)
      if (error?.status === 409) {
        toast.error(t('public.revealDigitalFootprint.emailExistsError'))
      } else {
        toast.error(t('feedback.toast.invalidOTP'))
      }
    }
  }

  const handleResend = async () => {
    try {
      await resendOtp({ email }).unwrap()
      localStorage.setItem(LS_KEY, Date.now().toString())
      toast.success(t('feedback.toast.OTPCode'))
    } catch (error) {
      catchWithSentry(error)
      toast.error(t('feedback.toast.errorOTP'))
    }
  }

  useEffect(() => {
    if (!email) {
      navigate(AppRoutes.revealDigitalFootprint, { replace: true })
    }
  }, [email, navigate])

  return (
    <WeScreen useHeader={false} customViewStyles="max-sm:p-0">
      <div className="flex w-full h-full items-center justify-center">
        <OTPVerificationForm
          email={email}
          onVerify={handleVerify}
          onResend={handleResend}
          isLoadingVerify={isLoadingVerify}
          isLoadingResend={isLoadingResend}
          resendCooldownSeconds={60}
        />
      </div>
    </WeScreen>
  )
}
